import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import useFiles, { filesKey, type FileEntry } from '../models/useFiles'
import type { FileTarget } from '../lib/webdavClient'
import { errorMessage } from '../utilities/general'
import BackListItem from './BackListItem'
import FileList from './FileList'
import FilePathSelect from './FilePathSelect'
import FileActionsMenu from './FileActionsMenu'
import FileDropZone from './FileDropZone'
import TextFieldForm from './TextFieldForm'

// The file browser for BOTH storage trees. A network's share and a node's own
// area are the same surface with a different target, so they are the same
// component — the alternative was two copies that drift, and the only real
// differences are the words around it.
export interface FileBrowserProps {
  target: FileTarget
  found: boolean // the network or node exists in the loaded data
  basePath: string // route prefix this browser links within
  defaultBackTo: string
  defaultBackLabel: string
  notFoundBackTo: string
  notFoundMessage: string
}

export default function FileBrowser({
  target,
  found,
  basePath,
  defaultBackTo,
  defaultBackLabel,
  notFoundBackTo,
  notFoundMessage,
}: FileBrowserProps) {
  const location = useLocation()
  const params = useParams<{ '*': string }>()
  const currentPath = params['*'] ?? ''

  const k = found ? filesKey(target, currentPath) : ''

  const entries = useFiles(state => (k ? state.entries[k] : undefined))
  const loading = useFiles(state => (k ? !!state.loading[k] : false))
  const error = useFiles(state => (k ? state.error[k] : null))
  const uploads = useFiles(state => (k ? state.uploads[k] : undefined))
  const list = useFiles(state => state.list)
  const refresh = useFiles(state => state.refresh)
  const upload = useFiles(state => state.upload)
  const download = useFiles(state => state.download)
  const remove = useFiles(state => state.remove)
  const createFolder = useFiles(state => state.createFolder)
  const rename = useFiles(state => state.rename)

  const [newFolderOpen, setNewFolderOpen] = useState(false)

  // ⚠ GATED ON `found`, NOT ON `target` — the old guard could never fire.
  // `target` is a required prop and always a truthy object built straight from
  // the route params, so `if (!target) return` was unreachable and the browser
  // listed against an id nothing had verified yet. That means a request for a
  // node or network the caller may not be allowed to see (the node tree answers
  // 403 below Owner/Admin, 404 outside the org) issued before the app knows
  // whether the id is even real. `found` is the app's answer to that question,
  // and `k` above already gates every read on it — this makes the write agree.
  useEffect(() => {
    if (!found) return
    list(target, currentPath)
  }, [found, target, currentPath, list])

  if (!found) {
    return (
      <Stack sx={{
        gap: 3
      }}>
        <BackListItem to={notFoundBackTo}>Files</BackListItem>
        <Typography color="textSecondary">{notFoundMessage}</Typography>
      </Stack>
    )
  }

  const backTo = typeof location.state?.backTo === 'string' ? location.state.backTo : defaultBackTo
  const backLabel = typeof location.state?.backLabel === 'string' ? location.state.backLabel : defaultBackLabel

  const handleUpload = async (files: File[]) => {
    const results = await Promise.allSettled(files.map(f => upload(target, currentPath, f)))
    await refresh(target, currentPath)
    const failed = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected')
    if (failed.length) alert(`${failed.length} upload(s) failed: ${errorMessage(failed[0].reason)}`)
  }

  const handleDownload = (entry: FileEntry) => {
    download(target, entry.filename, entry.basename).catch(err =>
      alert(`Download of ${entry.basename} failed: ${errorMessage(err)}`)
    )
  }

  const handleDelete = async (entry: FileEntry) => {
    try {
      await remove(target, entry.filename)
    } catch (err) {
      alert(`Delete of ${entry.basename} failed: ${errorMessage(err)}`)
    }
  }

  const handleRename = async (entry: FileEntry, newBasename: string) => {
    try {
      await rename(target, entry.filename, newBasename)
    } catch (err) {
      alert(`Rename of ${entry.basename} failed: ${errorMessage(err)}`)
    }
  }

  const handleCreateFolder = async (name: string) => {
    const trimmed = name.trim()
    if (!trimmed) return
    try {
      await createFolder(target, currentPath, trimmed)
    } catch (err) {
      alert(`Create folder "${trimmed}" failed: ${errorMessage(err)}`)
    }
  }

  return (
    <FileDropZone onFiles={handleUpload}>
      <Stack sx={{
        gap: 3
      }}>
        <BackListItem to={backTo}>{backLabel}</BackListItem>

        <Stack>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
            <FilePathSelect basePath={basePath} currentPath={currentPath} state={location.state} />
            <FileActionsMenu
              onRefresh={() => refresh(target, currentPath)}
              onNewFolder={() => setNewFolderOpen(v => !v)}
              onUpload={handleUpload}
              loading={loading}
            />
          </Stack>
          <Collapse in={newFolderOpen} unmountOnExit>
            <TextFieldForm
              autoFocus
              placeholder="Folder"
              onSubmit={handleCreateFolder}
              onCancel={() => setNewFolderOpen(false)}
              sx={{ marginTop: 2 }}
            />
          </Collapse>
        </Stack>

        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}

        {loading && !entries ? (
          <Stack
            sx={{
              alignItems: 'center',
              marginY: 4
            }}>
            <CircularProgress size={24} />
          </Stack>
        ) : (
          <FileList
            entries={entries ?? []}
            basePath={basePath}
            currentPath={currentPath}
            onFileClick={handleDownload}
            onRename={handleRename}
            onDelete={handleDelete}
            state={location.state}
            uploads={uploads}
          />
        )}
      </Stack>
    </FileDropZone>
  )
}
